// // src/components/seating-chart/user/ChartEventSeatPicker.js  (NEW FILE)
// //
// // Drop-in counterpart of <SeatsChart/> for events that have a chartEvent.
// // Rendering mirrors AdminChartEventSeatMap (same canvas element z-order, sections,
// // SeatingChartImage) but the interaction model is the buyer one.
// //
// // Props
// //   chartEventId, chartId, holdTtlMinutes   from eventData.chartEvent (or session.chartEvent)
// //   ticketTypes                              the event's/session's ticket types; each needs
// //                                            { id, name, price, isPublished, available, ticketOrder,
// //                                              seatingChartCategories { id } }
// //   quantities                               [{ id, quantity, ... }]  tickets chosen so far (your localQuantity / ticketQuantities)
// //   onBudgetChange(budget)                   called when auto-increase / auto-decrease changes tickets
// //   onSelectionChange({ isComplete, selectedCount, totalTickets })
// //   maxTotalTickets, otherCartTickets        optional cart limits
// //   currency                                 e.g. "RON" (display only)
// //
// // Ref API (use forwardRef): getCartItems(), commit(), clearSelection(), isComplete()

// import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
// import { Circle, Group, Layer, Rect, Stage, Text } from "react-konva";
// import {
//   Box,
//   Button,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   IconButton,
//   List,
//   ListItemButton,
//   ListItemText,
//   Stack,
//   Typography,
// } from "@mui/material";

// import { useQueryWithAuth } from "src/graphql/instances";
// import { GET_SEATING_CHART } from "src/graphql/queries/seatingCharts";
// import { getNormalizedRect } from "src/utils/canvasGeometryUtils";
// import { CANVAS_ELEMENT_Z_ORDER } from "./SeatingChartCanvasStage";
// import SeatingChartImage from "./SeatingChartImage";
// import useChartViewport from "src/hooks/useChartViewport";
// import useSeatSelection from "src/hooks/useSeatSelection";


// const SEAT_RADIUS = 14;
// const RIM = 3;

// const COLORS = {
//   held: "#F5A623", // held by somebody else
//   booked: "#5a5a5a",
//   blocked: "#2b2b2b",
//   selected: "#00C853",
//   notForSale: "#3a3a3a",
// };

// const hexToRgb = (hex) => {
//   const c = hex.replace("#", "");
//   const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
//   const n = parseInt(full, 16);
//   return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
// };
// const darken = (hex, a = 0.45) => {
//   const { r, g, b } = hexToRgb(hex);
//   return `rgb(${Math.round(r * (1 - a))}, ${Math.round(g * (1 - a))}, ${Math.round(b * (1 - a))})`;
// };

// // One seat. memo + primitive props => a status change re-renders only the seats that changed.
// // const SeatNode = React.memo(function SeatNode({ id, x, y, outer, inner, innerRadius, ring, clickable, onTap, onHover, onLeave }) {
// //   return (
// //     <Group
// //       x={x}
// //       y={y}
// //       onTap={clickable ? () => onTap(id) : undefined}
// //       onClick={clickable ? () => onTap(id) : undefined}
// //       onMouseEnter={(e) => {
// //         if (clickable) e.target.getStage().container().style.cursor = "pointer";
// //         onHover(e, id);
// //       }}
// //       onMouseMove={(e) => onHover(e, id)}
// //       onMouseLeave={(e) => {
// //         e.target.getStage().container().style.cursor = "default";
// //         onLeave();
// //       }}
// //     >
// //       {/* generous invisible hit area - 14px seats are tiny under a thumb */}
// //       <Circle radius={SEAT_RADIUS + 5} fill="transparent" />
// //       {ring && <Circle radius={SEAT_RADIUS + 3} stroke={ring} strokeWidth={2} listening={false} />}
// //       <Circle radius={SEAT_RADIUS} fill={outer} listening={false} perfectDrawEnabled={false} />
// //       <Circle radius={innerRadius} fill={inner} listening={false} perfectDrawEnabled={false} />
// //     </Group>
// //   );
// // });
// const SeatNode = React.memo(function SeatNode({
//   id, x, y, outer, inner, innerRadius, ring, hollow, dash, clickable, onTap, onHover, onLeave,
// }) {
//   const handleTap = () => {
//     if (clickable) onTap(id);
//   };
//   return (
//     <Group
//       x={x}
//       y={y}
//       onTap={handleTap}
//       onClick={handleTap}
//       onMouseEnter={(e) => {
//         e.target.getStage().container().style.cursor = clickable ? "pointer" : "not-allowed";
//         onHover(e, id);
//       }}
//       onMouseMove={(e) => onHover(e, id)}
//       onMouseLeave={(e) => {
//         e.target.getStage().container().style.cursor = "default";
//         onLeave();
//       }}
//     >
//       <Circle radius={SEAT_RADIUS + 5} fill="transparent" />
//       {ring && <Circle radius={SEAT_RADIUS + 3} stroke={ring} strokeWidth={2} listening={false} />}
//       {hollow ? (
//         // taken / held: border only, no fill
//         <Circle radius={SEAT_RADIUS - 1} stroke={outer} strokeWidth={2} dash={dash} listening={false} perfectDrawEnabled={false} />
//       ) : (
//         <>
//           <Circle radius={SEAT_RADIUS} fill={outer} listening={false} perfectDrawEnabled={false} />
//           <Circle radius={innerRadius} fill={inner} listening={false} perfectDrawEnabled={false} />
//         </>
//       )}
//     </Group>
//   );
// });

// const UNAVAILABLE_TEXT = {
//   booked: "Already booked",
//   held: "Temporarily held by another buyer",
//   blocked: "Not available",
//   notForSale: "Not available for sale",
//   loading: "Loading…",
// };

// const formatClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// const ChartEventSeatPicker = forwardRef(function ChartEventSeatPicker(
//   {
//     chartEventId,
//     chartId,
//     holdTtlMinutes = 15,
//     ticketTypes,
//     quantities,
//     onBudgetChange,
//     onSelectionChange,
//     maxTotalTickets,
//     otherCartTickets,
//     initialHoldToken,
//     autoIncreaseWhen,
//     currency = "",
//     height = 560,
//   },
//   ref,
// ) {
//   const { data: chartData } = useQueryWithAuth(GET_SEATING_CHART, { variables: { id: chartId }, skip: !chartId });
//   const chart = chartData?.seatingChart;
//   const seats = chart?.seats;

//   const sel = useSeatSelection({
//     chartEventId,
//     seats,
//     ticketTypes,
//     quantities,
//     onBudgetChange,
//     initialHoldToken,
//     holdTtlMinutes,
//     maxTotalTickets,
//     otherCartTickets,
//     autoIncreaseWhen,
//   });

//   useImperativeHandle(
//     ref,
//     () => ({
//       getCartItems: sel.getCartItems,
//       commit: sel.commit,
//       clearSelection: sel.clearSelection,
//       isComplete: () => sel.isComplete,
//     }),
//     [sel],
//   );

//   useEffect(() => {
//     onSelectionChange?.({
//       isComplete: sel.isComplete,
//       selectedCount: sel.selected.length,
//       totalTickets: sel.totalTickets,
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [sel.isComplete, sel.selected.length, sel.totalTickets]);

//   const categories = chart?.categories ?? [];
//   const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

//   // categories that can actually be bought (linked to a published ticket type with stock left)
//   const sellableCategoryIds = useMemo(() => {
//     const ids = new Set();
//     sel.eligibility.forEach((types, categoryId) => {
//       if (types.some((t) => (sel.ticketTypesById[t]?.available ?? 1) > 0)) ids.add(categoryId);
//     });
//     return ids;
//   }, [sel.eligibility, sel.ticketTypesById]);

//   const priceRangeFor = (categoryId) => {
//     const prices = (sel.eligibility.get(categoryId) ?? [])
//       .map((t) => sel.ticketTypesById[t]?.price)
//       .filter((p) => p !== undefined);
//     if (!prices.length) return null;
//     const min = Math.min(...prices);
//     const max = Math.max(...prices);
//     return min === max ? `${min} ${currency}` : `${min} – ${max} ${currency}`;
//   };

//   const canvasElements = useMemo(
//     () =>
//       [...(chart?.canvasElements ?? [])].sort(
//         (a, b) => (CANVAS_ELEMENT_Z_ORDER[a.type] ?? 0) - (CANVAS_ELEMENT_Z_ORDER[b.type] ?? 0),
//       ),
//     [chart?.canvasElements],
//   );

//   const vp = useChartViewport(chart);
//   const [hover, setHover] = useState(null);

//   const toggleRef = React.useRef(sel.toggleSeat);
//   toggleRef.current = sel.toggleSeat;

//   const onHover = React.useCallback((e, id) => {
//     const box = e.target.getStage().container().getBoundingClientRect();
//     const pos = e.target.getStage().getPointerPosition();
//     if (pos) setHover({ id, x: box.left + pos.x, y: box.top + pos.y });
//   }, []);
//   const onLeave = React.useCallback(() => setHover(null), []);
//   const lastTap = React.useRef({ id: null, at: 0 });
//   const onTap = React.useCallback(
//     (id) => {
//       if (vp.dragged.current) return; // that "click" was the end of a pan
//       // touch devices can deliver tap AND a synthesized click for one finger press -> only act once
//       const t = Date.now();
//       if (lastTap.current.id === id && t - lastTap.current.at < 500) return;
//       lastTap.current = { id, at: t };
//       toggleRef.current(id)
//     },
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//     [],
//   );

//   // const visualFor = (seat) => {
//   //   const color = categoryById.get(seat.categoryId)?.color ?? "#777";
//   //   const status = sel.statusById[seat.id] ?? "AVAILABLE";
//   //   if (sel.selectedIds.has(seat.id)) {
//   //     return { outer: "#fff", inner: COLORS.selected, innerRadius: SEAT_RADIUS - RIM, ring: COLORS.selected, clickable: true };
//   //   }
//   //   if (status === "BOOKED") return { outer: COLORS.booked, inner: COLORS.booked, innerRadius: SEAT_RADIUS * 0.5, clickable: false };
//   //   if (status === "BLOCKED") return { outer: COLORS.blocked, inner: COLORS.blocked, innerRadius: SEAT_RADIUS * 0.5, clickable: false };
//   //   if (status === "HELD") return { outer: COLORS.held, inner: COLORS.held, innerRadius: SEAT_RADIUS * 0.5, clickable: false };
//   //   if (!sellableCategoryIds.has(seat.categoryId)) {
//   //     return { outer: COLORS.notForSale, inner: COLORS.notForSale, innerRadius: SEAT_RADIUS - RIM, clickable: false };
//   //   }
//   //   return { outer: darken(color), inner: color, innerRadius: SEAT_RADIUS - RIM, clickable: true };
//   // };
//   const seatState = (seat) => {
//     const color = categoryById.get(seat.categoryId)?.color ?? "#777";
//     const status = sel.statusById[seat.id] ?? "AVAILABLE";
//     if (!sel.statusesLoaded) return { kind: "loading", outer: COLORS.notForSale, hollow: true, clickable: false };
//     if (sel.selectedIds.has(seat.id)) {
//       return { kind: "selected", outer: "#fff", inner: color, innerRadius: SEAT_RADIUS - RIM, ring: color, clickable: true };
//     }
//     if (status === "BOOKED") return { kind: "booked", outer: COLORS.booked, hollow: true, clickable: false };
//     if (status === "HELD") return { kind: "held", outer: COLORS.held, hollow: true, dash: [4, 3], clickable: false };
//     if (status === "BLOCKED") return { kind: "blocked", outer: COLORS.blocked, hollow: true, clickable: false };
//     if (!sellableCategoryIds.has(seat.categoryId)) {
//       return { kind: "notForSale", outer: COLORS.notForSale, inner: COLORS.notForSale, innerRadius: SEAT_RADIUS - RIM, clickable: false };
//     }
//     return { kind: "free", outer: darken(color), inner: color, innerRadius: SEAT_RADIUS - RIM, clickable: true };
//   };

//   const hoverSeat = hover ? seats?.find((s) => s.id === hover.id) : null;
//   const hoverTypes = hoverSeat ? sel.eligibility.get(hoverSeat.categoryId) ?? [] : [];
//   const hoverState = hoverSeat ? seatState(hoverSeat) : null;

//   return (
//     <Box>
//       {/* summary bar */}
//       <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 1 }}>
//         <Typography variant="subtitle2">
//           {sel.selected.length} / {sel.totalTickets}
//         </Typography>
//         {sel.secondsLeft !== null && (
//           <Typography variant="subtitle2" color={sel.secondsLeft < 60 ? "error" : "text.secondary"}>
//             {formatClock(sel.secondsLeft)}
//           </Typography>
//         )}
//       </Stack>

//       <Box ref={vp.setContainer} sx={{ position: "relative", height, bgcolor: "#1a1a1a", borderRadius: 1, touchAction: "none" }}>
//         {!chart || !vp.hasFit ? (
//           <Box display="flex" alignItems="center" justifyContent="center" height="100%">
//             <CircularProgress />
//           </Box>
//         ) : (
//           <Stage ref={vp.stageRef} {...vp.stageProps}>
//             <Layer listening={false}>
//               {canvasElements.map((shape) => {
//                 if (shape.type === "rect")
//                   return <Rect key={shape.id} {...getNormalizedRect(shape)} fill="#3a3a3a" cornerRadius={8} rotation={shape.rotation} />;
//                 if (shape.type === "text") return <Text key={shape.id} {...shape} fill="white" />;
//                 if (shape.type === "image") return <SeatingChartImage key={shape.id} shape={shape} />;
//                 return null;
//               })}
//               {chart.sections.map((s) => (
//                 <Rect
//                   key={s.id}
//                   x={s.x}
//                   y={s.y}
//                   width={s.width}
//                   height={s.height}
//                   rotation={s.rotation}
//                   stroke="#555"
//                   dash={[6, 4]}
//                   cornerRadius={6}
//                 />
//               ))}
//             </Layer>
//             <Layer>
//               {seats.map((seat) => {
//                 const v = seatState(seat);
//                 return (
//                   <SeatNode
//                     key={seat.id}
//                     id={seat.id}
//                     x={seat.x}
//                     y={seat.y}
//                     outer={v.outer}
//                     inner={v.inner}
//                     innerRadius={v.innerRadius}
//                     ring={v.ring}
//                     clickable={v.clickable}
//                     hollow={v.hollow}
//                     dash={v.dash}
//                     onTap={onTap}
//                     onHover={onHover}
//                     onLeave={onLeave}
//                   />
//                 );
//               })}
//             </Layer>
//           </Stage>
//         )}

//         {/* zoom controls (pinch/wheel also work) */}
//         <Stack sx={{ position: "absolute", right: 8, top: 8 }} spacing={0.5}>
//           <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={() => vp.zoomBy(1.4)}>+</IconButton>
//           <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={() => vp.zoomBy(1 / 1.4)}>−</IconButton>
//           <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={vp.reset}>⤢</IconButton>
//         </Stack>

//         {hoverSeat && (
//           <Box
//             sx={{
//               position: "fixed",
//               left: hover.x + 15,
//               top: hover.y + 15,
//               bgcolor: "background.paper",
//               boxShadow: 3,
//               borderRadius: 1,
//               p: 1.25,
//               pointerEvents: "none",
//               zIndex: 10,
//               display: { xs: "none", md: "block" }, // no hover on touch
//             }}
//           >
//             <Typography variant="caption" color="text.secondary">
//               {[hoverSeat.rowLabel && `Row ${hoverSeat.rowLabel}`, `Seat ${hoverSeat.label}`].filter(Boolean).join(" · ")}
//             </Typography>
//             <Typography variant="body2">{categoryById.get(hoverSeat.categoryId)?.name}</Typography>
//             {UNAVAILABLE_TEXT[hoverState.kind] && (
//               <Typography variant="caption" display="block" color="error.main" sx={{ fontWeight: 600 }}>
//                 {UNAVAILABLE_TEXT[hoverState.kind]}
//               </Typography>
//             )}
//             {hoverState.clickable && hoverTypes.map((t) => (
//               <Typography key={t} variant="caption" display="block">
//                 {sel.ticketTypesById[t]?.name} · {sel.ticketTypesById[t]?.price} {currency}
//               </Typography>
//             ))}
//           </Box>
//         )}
//       </Box>

//       {/* legend */}
//       <Stack direction="row" flexWrap="wrap" gap={2} sx={{ p: 1.5 }}>
//         {categories
//           .filter((c) => sellableCategoryIds.has(c.id))
//           .map((c) => (
//             <Stack key={c.id} direction="row" alignItems="center" spacing={0.75}>
//               <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: c.color }} />
//               <Typography variant="caption">
//                 {c.name}
//                 {priceRangeFor(c.id) ? ` · ${priceRangeFor(c.id)}` : ""}
//               </Typography>
//             </Stack>
//           ))}
//         <Stack direction="row" alignItems="center" spacing={0.75}>
//           <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#888", border: "3px solid #fff", outline: "2px solid #888" }} />
//           <Typography variant="caption">Selected</Typography>
//         </Stack>
//         <Stack direction="row" alignItems="center" spacing={0.75}>
//           <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: `2px dashed ${COLORS.held}` }} />
//           <Typography variant="caption">Held by another buyer</Typography>
//         </Stack>
//         <Stack direction="row" alignItems="center" spacing={0.75}>
//           <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: `2px solid ${COLORS.booked}` }} />
//           <Typography variant="caption">Booked</Typography>
//         </Stack>
//       </Stack>

//       {/* a seat that several ticket types fit -> ask which one to add */}
//       <Dialog open={!!sel.chooser} onClose={sel.cancelChooser} fullWidth maxWidth="xs">
//         <DialogTitle>Add a ticket for this seat</DialogTitle>
//         <DialogContent>
//           <List>
//             {(sel.chooser?.options ?? []).map((t) => (
//               <ListItemButton key={t} onClick={() => sel.chooseTicketType(t)}>
//                 <ListItemText
//                   primary={sel.ticketTypesById[t]?.name}
//                   secondary={`${sel.ticketTypesById[t]?.price} ${currency}`}
//                 />
//               </ListItemButton>
//             ))}
//           </List>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={sel.cancelChooser}>Cancel</Button>
//         </DialogActions>
//       </Dialog>
//     </Box>
//   );
// });

// export default ChartEventSeatPicker;

// src/components/seating-chart/user/ChartEventSeatPicker.js  (NEW FILE)
//
// Drop-in counterpart of <SeatsChart/> for events that have a chartEvent.
// Rendering mirrors AdminChartEventSeatMap (same canvas element z-order, sections,
// SeatingChartImage) but the interaction model is the buyer one.
//
// Props
//   chartEventId, chartId, holdTtlMinutes   from eventData.chartEvent (or session.chartEvent)
//   ticketTypes                              the event's/session's ticket types; each needs
//                                            { id, name, price, isPublished, available, ticketOrder,
//                                              seatingChartCategories { id } }
//   quantities                               [{ id, quantity, ... }]  tickets chosen so far (your localQuantity / ticketQuantities)
//   onBudgetChange(budget)                   called when auto-increase / auto-decrease changes tickets
//   onSelectionChange({ isComplete, selectedCount, totalTickets })
//   maxTotalTickets, otherCartTickets        optional cart limits
//   currency                                 e.g. "RON" (display only)
//
// Ref API (use forwardRef): getCartItems(), commit(), clearSelection(), isComplete()

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Circle, Group, Layer, Rect, Stage, Text } from "react-konva";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";

import { useQueryWithAuth } from "src/graphql/instances";
import { GET_SEATING_CHART } from "src/graphql/queries/seatingCharts";
import { getNormalizedRect } from "src/utils/canvasGeometryUtils";
import { CANVAS_ELEMENT_Z_ORDER } from "./SeatingChartCanvasStage";
import SeatingChartImage from "./SeatingChartImage";
import useChartViewport from "src/hooks/useChartViewport";
import useSeatSelection from "src/hooks/useSeatSelection";

const SEAT_RADIUS = 14;
const RIM = 3;

const COLORS = {
  held: "#F5A623", // held by somebody else
  booked: "#5a5a5a",
  blocked: "#2b2b2b",
  selected: "#00C853",
  notForSale: "#3a3a3a",
};

const hexToRgb = (hex) => {
  const c = hex.replace("#", "");
  const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const darken = (hex, a = 0.45) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${Math.round(r * (1 - a))}, ${Math.round(g * (1 - a))}, ${Math.round(b * (1 - a))})`;
};

// One seat. memo + primitive props => a status change re-renders only the seats that changed.
// Handlers are ALWAYS attached and guard on `clickable` themselves, so nothing depends on
// react-konva removing listeners when a seat flips from free to taken.
const SeatNode = React.memo(function SeatNode({
  id, x, y, outer, inner, innerRadius, ring, hollow, dash, clickable, onTap, onHover, onLeave,
}) {
  const handleTap = () => {
    if (clickable) onTap(id);
  };
  return (
    <Group
      x={x}
      y={y}
      onTap={handleTap}
      onClick={handleTap}
      onMouseEnter={(e) => {
        e.target.getStage().container().style.cursor = clickable ? "pointer" : "not-allowed";
        onHover(e, id);
      }}
      onMouseMove={(e) => onHover(e, id)}
      onMouseLeave={(e) => {
        e.target.getStage().container().style.cursor = "default";
        onLeave();
      }}
    >
      {/* generous invisible hit area - 14px seats are tiny under a thumb */}
      <Circle radius={SEAT_RADIUS + 5} fill="transparent" />
      {ring && <Circle radius={SEAT_RADIUS + 3} stroke={ring} strokeWidth={2} listening={false} />}
      {hollow ? (
        // taken / held: border only, no fill
        <Circle
          radius={SEAT_RADIUS - 1}
          stroke={outer}
          strokeWidth={2}
          dash={dash}
          listening={false}
          perfectDrawEnabled={false}
        />
      ) : (
        <>
          <Circle radius={SEAT_RADIUS} fill={outer} listening={false} perfectDrawEnabled={false} />
          <Circle radius={innerRadius} fill={inner} listening={false} perfectDrawEnabled={false} />
        </>
      )}
    </Group>
  );
});

// what the hover tooltip says for seats that cannot be picked
const UNAVAILABLE_TEXT = {
  booked: "Already booked",
  held: "Temporarily held by another buyer",
  blocked: "Not available",
  notForSale: "Not available for sale",
  loading: "Loading…",
};

const formatClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const ChartEventSeatPicker = forwardRef(function ChartEventSeatPicker(
  {
    chartEventId,
    chartId,
    holdTtlMinutes = 15,
    ticketTypes,
    quantities,
    onBudgetChange,
    onSelectionChange,
    maxTotalTickets,
    otherCartTickets,
    autoIncreaseWhen,
    initialHoldToken,
    currency = "",
    height = 560,
  },
  ref,
) {
  const { data: chartData } = useQueryWithAuth(GET_SEATING_CHART, { variables: { id: chartId }, skip: !chartId });
  const chart = chartData?.seatingChart;
  const seats = chart?.seats;

  const sel = useSeatSelection({
    chartEventId,
    seats,
    ticketTypes,
    quantities,
    onBudgetChange,
    initialHoldToken,
    holdTtlMinutes,
    maxTotalTickets,
    otherCartTickets,
    autoIncreaseWhen,
  });

  useImperativeHandle(
    ref,
    () => ({
      getCartItems: sel.getCartItems,
      commit: sel.commit,
      clearSelection: sel.clearSelection,
      releaseDeferred: sel.releaseDeferred,
      isComplete: () => sel.isComplete,
    }),
    [sel],
  );

  useEffect(() => {
    onSelectionChange?.({
      isComplete: sel.isComplete,
      selectedCount: sel.selected.length,
      totalTickets: sel.totalTickets,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.isComplete, sel.selected.length, sel.totalTickets]);

  const categories = chart?.categories ?? [];
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // categories that can actually be bought (linked to a published ticket type with stock left)
  const sellableCategoryIds = useMemo(() => {
    const ids = new Set();
    sel.eligibility.forEach((types, categoryId) => {
      if (types.some((t) => (sel.ticketTypesById[t]?.available ?? 1) > 0)) ids.add(categoryId);
    });
    return ids;
  }, [sel.eligibility, sel.ticketTypesById]);

  const priceRangeFor = (categoryId) => {
    const prices = (sel.eligibility.get(categoryId) ?? [])
      .map((t) => sel.ticketTypesById[t]?.price)
      .filter((p) => p !== undefined);
    if (!prices.length) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `${min} ${currency}` : `${min} – ${max} ${currency}`;
  };

  const canvasElements = useMemo(
    () =>
      [...(chart?.canvasElements ?? [])].sort(
        (a, b) => (CANVAS_ELEMENT_Z_ORDER[a.type] ?? 0) - (CANVAS_ELEMENT_Z_ORDER[b.type] ?? 0),
      ),
    [chart?.canvasElements],
  );

  const vp = useChartViewport(chart);
  const [hover, setHover] = useState(null);

  const onHover = React.useCallback((e, id) => {
    const box = e.target.getStage().container().getBoundingClientRect();
    const pos = e.target.getStage().getPointerPosition();
    if (pos) setHover({ id, x: box.left + pos.x, y: box.top + pos.y });
  }, []);
  const onLeave = React.useCallback(() => setHover(null), []);
  const toggleRef = React.useRef(sel.toggleSeat);
  toggleRef.current = sel.toggleSeat;
  const lastTap = React.useRef({ id: null, at: 0 });
  const onTap = React.useCallback(
    (id) => {
      if (vp.dragged.current) return; // that "click" was the end of a pan
      // touch devices can deliver tap AND a synthesized click for one finger press -> only act once
      const t = Date.now();
      if (lastTap.current.id === id && t - lastTap.current.at < 500) return;
      lastTap.current = { id, at: t };
      toggleRef.current(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // kind: selected | free | booked | held | blocked | notForSale | loading
  const seatState = (seat) => {
    const color = categoryById.get(seat.categoryId)?.color ?? "#777";
    // a cart seat the buyer un-selected is still HELD on the server but is theirs to re-pick
    const status = sel.deferredIds.has(seat.id) ? "AVAILABLE" : sel.statusById[seat.id] ?? "AVAILABLE";
    if (!sel.statusesLoaded) {
      return { kind: "loading", outer: COLORS.notForSale, hollow: true, clickable: false };
    }
    if (sel.selectedIds.has(seat.id)) {
      return { kind: "selected", outer: "#fff", inner: color, innerRadius: SEAT_RADIUS - RIM, ring: color, clickable: true };
    }
    if (status === "BOOKED") return { kind: "booked", outer: COLORS.booked, hollow: true, clickable: false };
    if (status === "HELD") return { kind: "held", outer: color, hollow: true, dash: [4, 3], clickable: false };
    if (status === "BLOCKED") return { kind: "blocked", outer: COLORS.blocked, hollow: true, clickable: false };
    if (!sellableCategoryIds.has(seat.categoryId)) {
      return { kind: "notForSale", outer: COLORS.notForSale, inner: COLORS.notForSale, innerRadius: SEAT_RADIUS - RIM, clickable: false };
    }
    return { kind: "free", outer: darken(color), inner: color, innerRadius: SEAT_RADIUS - RIM, clickable: true };
  };

  const hoverSeat = hover ? seats?.find((s) => s.id === hover.id) : null;
  const hoverState = hoverSeat ? seatState(hoverSeat) : null;
  const hoverTypes = hoverSeat ? sel.eligibility.get(hoverSeat.categoryId) ?? [] : [];

  return (
    <Box>
      {/* summary bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1, py: 1 }}>
        <Typography variant="subtitle2">
          {sel.selected.length} / {sel.totalTickets}
        </Typography>
        {sel.secondsLeft !== null && (
          <Typography variant="subtitle2" color={sel.secondsLeft < 60 ? "error" : "text.secondary"}>
            {formatClock(sel.secondsLeft)}
          </Typography>
        )}
      </Stack>

      <Box ref={vp.setContainer} sx={{ position: "relative", height, bgcolor: "#1a1a1a", borderRadius: 1, touchAction: "none" }}>
        {!chart || !vp.hasFit ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Stage ref={vp.stageRef} {...vp.stageProps}>
            <Layer listening={false}>
              {canvasElements.map((shape) => {
                if (shape.type === "rect")
                  return <Rect key={shape.id} {...getNormalizedRect(shape)} fill="#3a3a3a" cornerRadius={8} rotation={shape.rotation} />;
                if (shape.type === "text") return <Text key={shape.id} {...shape} fill="white" />;
                if (shape.type === "image") return <SeatingChartImage key={shape.id} shape={shape} />;
                return null;
              })}
              {chart.sections.map((s) => (
                <Rect
                  key={s.id}
                  x={s.x}
                  y={s.y}
                  width={s.width}
                  height={s.height}
                  rotation={s.rotation}
                  stroke="#555"
                  dash={[6, 4]}
                  cornerRadius={6}
                />
              ))}
            </Layer>
            <Layer>
              {seats.map((seat) => {
                const v = seatState(seat);
                return (
                  <SeatNode
                    key={seat.id}
                    id={seat.id}
                    x={seat.x}
                    y={seat.y}
                    outer={v.outer}
                    inner={v.inner}
                    innerRadius={v.innerRadius}
                    ring={v.ring}
                    hollow={v.hollow}
                    dash={v.dash}
                    clickable={v.clickable}
                    onTap={onTap}
                    onHover={onHover}
                    onLeave={onLeave}
                  />
                );
              })}
            </Layer>
          </Stage>
        )}

        {/* zoom controls (pinch/wheel also work) */}
        <Stack sx={{ position: "absolute", right: 8, top: 8 }} spacing={0.5}>
          <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={() => vp.zoomBy(1.4)}>+</IconButton>
          <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={() => vp.zoomBy(1 / 1.4)}>−</IconButton>
          <IconButton size="small" sx={{ bgcolor: "background.paper" }} onClick={vp.reset}>⤢</IconButton>
        </Stack>

        {hoverSeat && (
          <Box
            sx={{
              position: "fixed",
              left: hover.x + 15,
              top: hover.y + 15,
              bgcolor: "background.paper",
              boxShadow: 3,
              borderRadius: 1,
              p: 1.25,
              pointerEvents: "none",
              zIndex: 10,
              display: { xs: "none", md: "block" }, // no hover on touch
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {[hoverSeat.rowLabel && `Row ${hoverSeat.rowLabel}`, `Seat ${hoverSeat.label}`].filter(Boolean).join(" · ")}
            </Typography>
            <Typography variant="body2">{categoryById.get(hoverSeat.categoryId)?.name}</Typography>
            {UNAVAILABLE_TEXT[hoverState.kind] && (
              <Typography variant="caption" display="block" color="error.main" sx={{ fontWeight: 600 }}>
                {UNAVAILABLE_TEXT[hoverState.kind]}
              </Typography>
            )}
            {hoverState.clickable && hoverTypes.map((t) => (
              <Typography key={t} variant="caption" display="block">
                {sel.ticketTypesById[t]?.name} · {sel.ticketTypesById[t]?.price} {currency}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* legend */}
      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ p: 1.5 }}>
        {categories
          .filter((c) => sellableCategoryIds.has(c.id))
          .map((c) => (
            <Stack key={c.id} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: c.color }} />
              <Typography variant="caption">
                {c.name}
                {priceRangeFor(c.id) ? ` · ${priceRangeFor(c.id)}` : ""}
              </Typography>
            </Stack>
          ))}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#888", border: "3px solid #fff", outline: "2px solid #888" }} />
          <Typography variant="caption">Selected</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: `2px dashed ${COLORS.booked}` }} />
          <Typography variant="caption">Held by another buyer</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: `2px solid ${COLORS.booked}` }} />
          <Typography variant="caption">Booked</Typography>
        </Stack>
      </Stack>

      {/* a seat that several ticket types fit -> ask which one to add */}
      <Dialog open={!!sel.chooser} onClose={sel.cancelChooser} fullWidth maxWidth="xs">
        <DialogTitle>Add a ticket for this seat</DialogTitle>
        <DialogContent>
          <List>
            {(sel.chooser?.options ?? []).map((t) => (
              <ListItemButton key={t} onClick={() => sel.chooseTicketType(t)}>
                <ListItemText
                  primary={sel.ticketTypesById[t]?.name}
                  secondary={`${sel.ticketTypesById[t]?.price} ${currency}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={sel.cancelChooser}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ChartEventSeatPicker;